<script lang="ts" setup>
// TODO: refactor to move mask logic to separated component
import { computed, ref, watch, withDefaults, ObjectDirective } from 'vue';
import { MaskTokens } from 'maska/dist/types/tokens';
import { Mask } from 'maska';
// instructions for maska lib you can find here
// https://beholdr.github.io/maska/#/?id=live-demo

const props = withDefaults(defineProps<{
  placeholder?: string;
  modelValue?: string;
  type?: string;
  isError?: boolean;
  returnMaskedValue?: boolean;
  mask?: string;
  maskTokens?: MaskTokens;
  disallowSpecialChars?: boolean;
  disallowNumbers?: boolean,
  allowIntegerOnly?: boolean,
  allowChars?: string[],
  disallowChars?: string[],
  append?: boolean,
  prepend?: boolean,
  readonly?: boolean,
  disabled?: boolean,
  autoFocused?: boolean
}>(), {
  modelValue: '',
  type: 'text',
  maskTokens: () => ({}),
});

const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>();

const localValue = ref('');
const focused = ref(false);

const mask = new Mask({
  mask: props.mask,
  tokens: props.maskTokens,
});
const unmaskedLocalValue = computed(() => mask.unmasked(localValue.value));
const returnValue = computed(() => (
  (!props.mask || props.returnMaskedValue) ? localValue.value : unmaskedLocalValue.value
));

function getEscapedRegExp(str: string) {
  // If you need to use any of the special characters literally (actually searching for a "*", for instance),
  // you must escape it by putting a backslash in front of it.
  // eslint-disable-next-line no-useless-escape
  return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
}

const specialCharacters = computed(() => {
  // depending on props we are adding or removing characters
  let charsString = '';
  if (props.disallowSpecialChars) charsString += '@&#+$~%^:*?<>{}[]!)(_/\\|=`;"';
  if (props.disallowNumbers) charsString += '0123456789';
  if (props.disallowChars) charsString += props.disallowChars;
  if (props.allowChars) {
    [...charsString].forEach((char) => {
      charsString = charsString.replace(char, '');
    });
  }
  return getEscapedRegExp(charsString);
});
// create regex from characters what to exclude
const disallowCharsRegex = computed(() => new RegExp(`[${specialCharacters.value}]`, 'g'));

function onFilterSpecialCharsKeyDown(event: KeyboardEvent) {
  // function to exclude characters on typing
  if (specialCharacters.value.includes(event.key)) {
    event.preventDefault();
  }
}

function onFilterSpecialCharsBeforeInput(event: InputEvent) {
  if (disallowCharsRegex.value.test(event.data || '')) {
    event.preventDefault();
  }
}

function onIntegerKeyDown(event: KeyboardEvent) {
  if (['.', ',', '+', '-', 'e', 'E'].includes(event.key)) {
    event.preventDefault();
  }
}

function onIntegerBeforeInput(event: InputEvent) {
  if (event.data === '.') event.preventDefault();
}

function onBeforeInput(event: InputEvent) {
  if (props.allowIntegerOnly) onIntegerBeforeInput(event);
  onFilterSpecialCharsBeforeInput(event);
}

function onKeyDown(event: KeyboardEvent) {
  if (props.allowIntegerOnly) onIntegerKeyDown(event);
  onFilterSpecialCharsKeyDown(event);
}

function onInput(value: string) {
  // characters to check to replace
  const charsToCheck = props.allowIntegerOnly ? /^0+|\D+/g : disallowCharsRegex.value;
  // set filtered value
  localValue.value = value.replace(charsToCheck, '');
  // if maska then set masked value
  if (props.mask) localValue.value = mask.masked(localValue.value);
  emit('update:modelValue', returnValue.value);
}

function onInputEvent() {
  onInput(localValue.value);
}

// On user paste from buffer
function onPaste(event: ClipboardEvent) {
  const clipboardData = event?.clipboardData?.getData('text/plain');
  const clipboardDataTrimmed = String(clipboardData)
    .replace(/\t/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();
  // wait until variable changed
  setTimeout(() => onInput(clipboardDataTrimmed));
}

function onFocus() {
  focused.value = true;
}

function onBlur() {
  focused.value = false;
}
const vFocus: ObjectDirective<HTMLElement, boolean | undefined> = {
  mounted: (el, { value }) => value && el.focus(),
};
// check modelValue data
if (props.modelValue !== '') onInput(props.modelValue);
watch(() => props.modelValue, () => onInput(props.modelValue));
</script>

<template>
  <div
    class="BaseFormInput base-form-input"
    :class="{'is--error': isError, 'is--focused': focused, 'is--readonly': readonly, 'is--disabled': disabled}"
  >
    <span
      v-if="append"
      class="base-form-input__append"
    >
      <slot name="append" />
    </span>
    <input
      v-model="localValue"
      v-focus="autoFocused"
      class="base-form-input__input"
      :placeholder="placeholder"
      :type="type"
      :title="localValue"
      tabindex="0"
      v-bind="$attrs"
      @input="onInputEvent"
      @paste="onPaste"
      @keydown="onKeyDown"
      @beforeinput="onBeforeInput"
      @focus="onFocus"
      @blur="onBlur"
    >
    <span
      v-if="prepend"
      class="base-form-input__prepend"
    >
      <slot name="prepend" />
    </span>
  </div>
</template>

<style lang="sass">
@import '~Styles/_variables'
.base-form-input
  padding: 0 15px
  color: $primary
  caret-color: $primary
  background-color: white
  font-size: $base-font-size
  line-height: $base-line-height
  margin-top: 0
  margin-bottom: 0
  +font(400)
  -webkit-appearance: none
  display: flex
  align-items: center
  width: 100%
  position: relative
  border: solid 2px transparent
  background-color: $gray-10
  height: 48px
  &:before
    content: ''
    position: absolute
    left: -2px
    bottom: -2px
    width: calc(100% + 4px)
    height: 100%
    border-bottom: solid 1px $tones-gray-50
    z-index: 1
  &.is--focused
    border-color: $denim
    &:before
      opacity: 0
  &.is--error
    border-color: $support_red
    &:before
      opacity: 0
  &.is--readonly
    background-color: transparent
    pointer-events: none
  &.is--disabled
    opacity: 0.3
    pointer-events: none
  &__input
    padding: 0
    border: none
    color: inherit
    font-size: inherit
    font-weight: inherit
    font-family: inherit
    line-height: inherit
    background-color: transparent
    width: 100%
    position: relative
    z-index: 2

    &:focus
      outline: none

    &::placeholder
      opacity: 1
      +font(400)
      color: $input-field-placeholder-color
      font-size: $base-font-size
      line-height: 22px

  &__append,
  &__prepend
    color: inherit
    font-size: inherit
    font-weight: inherit
    font-family: inherit
    line-height: inherit
    width: 20px
    height: 100%
    display: flex
    align-items: center
    margin-right: 8px
    position: relative
    z-index: 2
    > div
      width: 20px
      height: 20px
    svg
      width: 20px
      height: 20px
      fill: inherit
  &__prepend
    margin-right: 0
    margin-left: 8px
</style>
